import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Contact } from '../../../models/contact.model';
import { Service, ServiceSelectMode } from '../../../models/service.model';
import { SharedModule } from '../../../shared/shared.module';
import {
  closeDropdown,
  closeDropdownOnOutsideClick,
  stopEvent,
  toggleDropdown,
} from '../../../shared/utils/dropdown.util';
import {
  InlineErrors,
  updateInlineErrors,
} from '../../../shared/utils/form-validation.util';
import {
  createBusinessDateValidator,
  isObservedHoliday,
  isValidBusinessDateAfterDays,
} from '../../../shared/utils/ticket-date-rules.util';

@Component({
  selector: 'app-ticket-disconnect',
  templateUrl: './ticket-disconnect.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    SharedModule,
  ],
})
export class TicketDisconnectComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);

  ServiceSelectMode = ServiceSelectMode;

  private disconnectDateValidator = createBusinessDateValidator(30);

  public disconnectTxGroup: UntypedFormGroup = this.fb.group({
    reason: this.fb.control('', Validators.required),
    otherReasonDetails: this.fb.control(''),
    comments: this.fb.control(''),
    reporter: this.fb.control('', Validators.required),
    preferredContactMethod: this.fb.control('', Validators.required),
    countryCode: this.fb.control(''),

    services: this.fb.array([
      this.fb.group({
        identifyIssueBy: this.fb.control('location'),
        serviceId: this.fb.control(null, Validators.required),
        locationId: this.fb.control(null, Validators.required),
        locationAddress: this.fb.control(null),
        desiredDate: this.fb.control(null, [
          Validators.required,
          this.disconnectDateValidator,
        ]),
        servicesByLocation: this.fb.array([
          this.fb.group({
            id: this.fb.control(''),
            disabled: this.fb.control(''),
            nit: this.fb.control(''),
            product: this.fb.control(''),
            locationId: this.fb.control(''),
            locationName: this.fb.control(''),
          }),
        ]),
      }),
    ]),
  });

  @Input() services: Service[];

  @Input() selectedServiceId: string;

  @Input() submissionError: boolean;

  @Input() isSubmitting: boolean;

  @Input() primaryReporters: Contact[];

  @Input() errorMessage: string;

  @Input() disconnectReasons: string[];

  @Output() submitForm = new EventEmitter();

  formSubmitAttempted = signal(false);

  inlineErrors: InlineErrors = {};

  areServicesLoading = signal(true);

  areUsersLoading = signal(true);

  isReasonDropdownOpen = signal(false);

  private elementRef = inject(ElementRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    closeDropdownOnOutsideClick(this.elementRef, event, this.isReasonDropdownOpen);
  }

  get selectedReasonLabel(): string {
    return this.disconnectTxGroup?.value?.reason ?? '';
  }

  toggleReasonDropdown(event: Event): void {
    stopEvent(event);
    toggleDropdown(this.isReasonDropdownOpen);
  }

  selectReason(reason: string, event: Event): void {
    stopEvent(event);
    this.disconnectTxGroup.controls.reason.setValue(reason);
    closeDropdown(this.isReasonDropdownOpen);
    this.canSubmit();
  }

  ngOnInit(): void {
    this.canSubmit();
  }

  filterDates = (d: Date): boolean => isValidBusinessDateAfterDays(d, 30);

  isHoliday(calendarDate: Date): boolean {
    return isObservedHoliday(calendarDate);
  }

  canSubmit(): boolean {
    return updateInlineErrors(
      this.disconnectTxGroup,
      this.inlineErrors,
      {},
      { clearValidWith: 'delete' },
    );
  }

  submitTicket(): void {
    this.formSubmitAttempted.set(true);
    if (this.canSubmit()) {
      const request = {
        order_type: 'Disconnect_Order',
        disconnectReason: this.disconnectTxGroup.value.reason,
        disconnectReasonDetails:
          this.disconnectTxGroup.value.otherReasonDetails,
        contactId: this.disconnectTxGroup.value.reporter,
        additionalComment: this.disconnectTxGroup.value.comments,
        preferredContactMethod:
          this.disconnectTxGroup.value.preferredContactMethod,
        lineItems: this.disconnectTxGroup.value.services,
      };
      this.submitForm.emit(request);
    }
  }

  usersLoaded(): void {
    this.areUsersLoading.set(false);
  }

  servicesLoaded(): void {
    this.areServicesLoading.set(false);
  }
}
